import React, { useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import ChatIcon from "@mui/icons-material/Chat";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import { v4 as uuidv4 } from "uuid";
import {
  MAX_LENGTH_INPUT_SEARCH,
  AGENT_ENDPOINT_URL,
} from "../env.js";
import Answering from "./Answering.js";
import MarkdownRenderer from "./MarkdownRenderer.js";
import DynamicRecommendedPrompts from "./DynamicRecommendedPrompts.js";
import StaticRecommendedPrompts from "./StaticRecommendedPrompts.js";
import "./ChatWidget.css";

const ChatWidget = ({ userName = "Guest User" }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [enabled, setEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [answers, setAnswers] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [sessionId] = React.useState(uuidv4());
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isMaximized, setIsMaximized] = React.useState(false);
  const scrollRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const autoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);

  useEffect(() => {
    if (!chatMessagesRef.current || !autoScrollRef.current) return;
    chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
  }, [answers]);

  const handleScroll = () => {
    if (!chatMessagesRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
    const isScrollingUp = scrollTop < lastScrollTopRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    lastScrollTopRef.current = scrollTop;
    
    if (isScrollingUp) {
      autoScrollRef.current = false;
    } else if (isAtBottom) {
      autoScrollRef.current = true;
    }
  };

  const handleQuery = (event) => {
    const newValue = event.target.value.replace(/\n/g, "");
    setQuery(newValue);
    if (newValue.length > 0 && loading === false)
      setEnabled(true);
    else setEnabled(false);
  };

  const handleKeyPress = (event) => {
    if (event.code === "Enter" && loading === false && query !== "") {
      getAnswer(query);
    }
  };

  const handleClick = async (e) => {
    e.preventDefault();
    if (query !== "") {
      getAnswer(query);
    }
  };

  const getAnswer = async (my_query) => {
    if (!loading && my_query !== "") {
      setAnswers((prevState) => [...prevState, { query: my_query }]);
      setEnabled(false);
      setLoading(true);
      setErrorMessage("");
      setQuery("");
      autoScrollRef.current = true;
      try {
        const queryUuid = uuidv4();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        let json = {
          text: "",
          queryUuid,
        };

        setAnswers((prevState) => [...prevState, json]);

        const params = {
          prompt: my_query,
          prompt_uuid: queryUuid,
          user_timezone: timezone,
          session_id: sessionId,
        };

        const response = await fetch(AGENT_ENDPOINT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let responseText = "";

        while (true) {
          const { value, done } = await reader.read();

          if (done) {
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          responseText += chunk;

          setAnswers((prev) => {
            const newAnswers = [...prev];
            const lastIndex = newAnswers.length - 1;
            newAnswers[lastIndex] = {
              ...newAnswers[lastIndex],
              text: (newAnswers[lastIndex].text || '') + chunk,
            };
            return newAnswers;
          });
        }

        setLoading(false);
        setEnabled(false);

      } catch (error) {
        console.log("Call failed: ", error);
        setErrorMessage(error.toString());
        setLoading(false);
        setEnabled(false);
      }
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (window.self !== window.top) {
      window.parent.postMessage({ type: 'widget-state', isOpen: true }, '*');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (window.self !== window.top) {
      window.parent.postMessage({ type: 'widget-state', isOpen: false }, '*');
    }
  };

  const toggleMaximize = () => {
    const newMaximized = !isMaximized;
    setIsMaximized(newMaximized);
    // Notify parent to resize iframe
    if (window.self !== window.top) {
      window.parent.postMessage({ type: 'widget-resize', maximized: newMaximized }, '*');
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button className="chat-widget-button" onClick={handleOpen}>
          <ChatIcon sx={{ fontSize: 28 }} />
        </button>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <div className={`chat-widget-modal ${isOpen ? 'open' : ''} ${isMaximized ? 'maximized' : ''}`}>
          {/* Header */}
          <div className="chat-widget-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img
                src="/images/shipa-logo.svg"
                alt="SHIPA AI"
                height={24}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={toggleMaximize} size="small" sx={{ color: '#ffffff' }}>
                {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <IconButton onClick={handleClose} size="small" sx={{ color: '#ffffff' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </div>

          {/* Messages */}
          <div className="chat-widget-messages" ref={chatMessagesRef} onScroll={handleScroll}>
            {answers.length === 0 ? (
              <div className="chat-widget-welcome">
                <img
                  src="/images/shipa-logo.svg"
                  alt="SHIPA Analytics"
                  height={80}
                />
                <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
                  {t('app.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('chat.description')}
                </Typography>
                <StaticRecommendedPrompts
                  show={true}
                  onPromptClick={(prompt) => {
                    setQuery(prompt);
                    getAnswer(prompt);
                  }}
                />
              </div>
            ) : (
              <ul style={{ padding: 0, margin: 0, listStyleType: "none" }}>
                {answers.map((answer, index) => (
                  <li key={"msg" + index}>
                    {answer.hasOwnProperty("query") && answer.query !== "" && (
                      <div className="chat-widget-message user">
                        <div className="message-bubble user-bubble">
                          <Typography variant="body2">
                            {answer.query}
                          </Typography>
                        </div>
                      </div>
                    )}

                    {answer.hasOwnProperty("text") && answer.text !== "" && (
                      <>
                        <div className="chat-widget-message bot">
                          <img
                            src="/images/shipa-logo.svg"
                            alt="SHIPA AI"
                            className="bot-avatar"
                          />
                          <div className="message-bubble bot-bubble">
                            <Typography component="div" variant="body2">
                              <MarkdownRenderer content={answer.text} />
                            </Typography>
                          </div>
                        </div>

                        {index === answers.length - 1 && !loading && (
                          <DynamicRecommendedPrompts
                            answer={answer}
                            userQuery={answers[index - 1]?.query || ""}
                            answerIndex={index}
                            onPromptClick={(prompt) => {
                              setQuery(prompt);
                              getAnswer(prompt);
                            }}
                          />
                        )}
                      </>
                    )}
                  </li>
                ))}

                {loading && (
                  <Box sx={{ pl: 2, mb: 2, mt: 1 }}>
                    <Answering loading={loading} />
                  </Box>
                )}

                <li ref={scrollRef} />
              </ul>
            )}
          </div>

          {/* Error Message */}
          {errorMessage !== "" && (
            <div className="chat-widget-error">
              <Typography variant="caption" color="error">
                {errorMessage}
              </Typography>
            </div>
          )}

          {/* Input */}
          <div className="chat-widget-input">
            <img
              src="/images/shipa-logo.svg"
              alt="SHIPA"
              height={20}
              style={{ marginRight: 8 }}
            />
            <InputBase
              required
              id="query"
              name="query"
              placeholder={t('chat.placeholder')}
              fullWidth
              onChange={handleQuery}
              onKeyDown={handleKeyPress}
              value={query}
              inputProps={{ maxLength: MAX_LENGTH_INPUT_SEARCH }}
              sx={{ flex: 1, fontSize: '0.9rem' }}
            />
            <IconButton
              color="primary"
              size="small"
              disabled={!enabled}
              onClick={handleClick}
            >
              <SendIcon />
            </IconButton>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
